import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useDispatch } from 'react-redux'
// components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/view'
import ReviewQuestionsForm from './components/Form'
import Picklists from './components/Picklists'
// icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
// constants
import { Modals } from 'constants/index'
// hooks
import { useShowModal } from 'hooks/index'
// graphql
import { FIELD_CREATE_MUTATION, FIELD_DELETE_MUTATION, OBJECT_ENTITY_UPDATE_MUTATION } from 'graphql/objects/mutations'
// store
import { updateObjectEntities } from 'store/reducers/objectEntities'
// themes
import { colors } from 'themes/index'
// context
import { ObjectsAPIContext } from 'containers/ObjectsAndProperties/Edit/layers/API'
// types
import { FieldType, IField, IObjectEntityReviewQuestionInput } from 'types/graphql'
// utils
import { v4 as uuid } from 'uuid'
import { get, orderBy } from 'lodash'
import { handleError } from 'utils/errors/handlers'

type FieldCreateResponse = { fieldCreate: { data: { id: string } } }

const ReviewQuestions: React.FC = () => {
  const dispatch = useDispatch()

  const [fieldCreateMutation] = useMutation<FieldCreateResponse>(FIELD_CREATE_MUTATION)
  const [fieldDeleteMutation] = useMutation(FIELD_DELETE_MUTATION)
  const [objectEntityUpdateMutation] = useMutation(OBJECT_ENTITY_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.PROPERTY_DETAILS_MODAL })

  const { object, refetch } = React.useContext(ObjectsAPIContext)

  const [isOpen, setIsOpen] = React.useState(true)
  const [reviewQuestions, setReviewQuestions] = React.useState(() => {
    if (object?.reviewQuestions?.length) {
      return object.reviewQuestions
    }

    return [
      {
        id: uuid(),
        text: 'What were the goals for this period and do you think you`ve achieved them?',
        position: 0,
        active: true
      },
      { id: uuid(), text: 'What have you done well?', position: 1, active: true },
      { id: uuid(), text: 'What could you have done better?', position: 2, active: true }
    ]
  })

  const picklists = React.useMemo(() => {
    const reviewFieldIds = object?.reviewFieldIds || []

    return orderBy(
      (object?.fields || []).filter((field) => reviewFieldIds.includes(field.id)),
      'position'
    )
  }, [object?.fields, object?.reviewFieldIds])

  const editPicklist = (field: IField) => showModal({ property: field })

  const syncObjectEntities = async () => {
    const { data: refetched } = await refetch()

    const freshObject = get(refetched, 'objectEntities.data[0]')

    if (freshObject) {
      dispatch(
        updateObjectEntities({
          id: object.id,
          data: { fields: freshObject.fields, reviewFieldIds: freshObject.reviewFieldIds }
        })
      )
    }

    return freshObject
  }

  const addPicklist = async () => {
    try {
      const input = {
        active: true,
        label: '',
        options: [],
        fieldType: FieldType.PickList,
        objectEntityId: object.id,
        position: object.fields.length
      }

      const {
        data: {
          fieldCreate: { data: createdField }
        }
      } = await fieldCreateMutation({ variables: { input } })

      const reviewFieldIds = [...(object.reviewFieldIds || []), createdField.id]

      await objectEntityUpdateMutation({ variables: { id: object.id, input: { reviewFieldIds } } })

      const freshObject = await syncObjectEntities()

      const property = freshObject.fields.find((field) => field.id === createdField.id)

      if (property) {
        showModal({ property })
      }
    } catch (errors) {
      handleError(errors, 'ReviewQuestions.addPicklist')
    }
  }

  const deletePicklist = async (field: IField) => {
    try {
      const reviewFieldIds = object.reviewFieldIds.filter((id) => id !== field.id)

      await fieldDeleteMutation({ variables: { id: field.id } })

      await objectEntityUpdateMutation({ variables: { id: object.id, input: { reviewFieldIds } } })

      await syncObjectEntities()
    } catch (errors) {
      handleError(errors, 'ReviewQuestions.deletePicklist')
    }
  }

  const sortedQuestions = orderBy(reviewQuestions, 'position')

  const initialValues = Object.fromEntries(sortedQuestions.map((item) => [item.id, item.text]))

  const saveReviewQuestions = async (reviewQuestions: IObjectEntityReviewQuestionInput[]) => {
    await objectEntityUpdateMutation({
      variables: {
        id: object.id,
        input: {
          reviewQuestions
        }
      }
    })

    refetch()

    dispatch(updateObjectEntities({ id: object.id, data: { reviewQuestions } }))
  }

  const onSubmit = async (data: Record<string, string>) => {
    try {
      const nextQuestions = Object.entries(data).map(([key, value], idx) => ({
        id: key,
        text: value,
        position: idx,
        active: reviewQuestions.find((item) => item.id === key).active
      }))

      await saveReviewQuestions(nextQuestions)

      setReviewQuestions(nextQuestions)
    } catch (errors) {
      handleError(errors, 'ReviewQuestions.onSubmit')
    }
  }

  const toggleQuestion = async (id: string) => {
    const nextQuestions = sortedQuestions.map(({ id: questionId, text, position, active }) => ({
      id: questionId,
      text,
      position,
      active: questionId === id ? !active : active
    }))

    // updated before the request, so a pending debounced text submit picks up the new state
    setReviewQuestions(nextQuestions)

    try {
      await saveReviewQuestions(nextQuestions)
    } catch (errors) {
      setReviewQuestions(reviewQuestions)

      handleError(errors, 'ReviewQuestions.toggleQuestion')
    }
  }

  const expandPanel = (_, expanded: boolean) => setIsOpen(expanded)

  return (
    <Accordion expanded={isOpen} onChange={expandPanel} isProperties>
      <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>Review Questions</AccordionSummary>
      <AccordionDetails>
        <ReviewQuestionsForm
          onSubmit={onSubmit}
          onToggle={toggleQuestion}
          questions={sortedQuestions}
          initialValues={initialValues}
        />
        <Picklists picklists={picklists} onEdit={editPicklist} onAdd={addPicklist} onDelete={deletePicklist} />
      </AccordionDetails>
    </Accordion>
  )
}

export default ReviewQuestions
